import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import Pill from '../../components/buttons/Pill';
import {Helmet} from "react-helmet";

const Options = () => {
    
    const dispatch = useDispatch();

    const visibleUnitsDaily = useSelector((state) => state.dailyForecast.visibleUnitsDaily) || {
        wave_height_max: true,
        wave_direction_dominant: false,
        wave_period_max: false,
        wind_speed_10m_max: false,
        wind_direction_10m_dominant: false,
        overall_rating_score: true
    };

    const visibleUnitsHourly = useSelector((state) => state.hourlyForecast.visibleUnitsHourly) || {
        overall_rating_score: true,
        wave_height: true,
        swell_wave_height: true,
        swell_wave_period: true,
        swell_wave_direction: true,
        wind_speed_10m: true,
        wind_speed_80m: false,
        wind_gusts_10m: false,
        wind_direction_10m: true,
        temperature_2m: false,
        rain: false,
        cloud_cover: false,
        sea_level_height_msl: false,
    };

    const includedHours = useSelector((state) => state.hourlyForecast.includedHours) || [
        '03:00', '06:00', '09:00', '12:00', '14:00', '16:00', '18:00', '20:00'
    ];

    const handleIncludedHoursChange = (hour) => {
        if (!includedHours) return;

        const newIncludedHours = includedHours.includes(hour)
            ? includedHours.filter(h => h !== hour)
            : [...includedHours, hour];

        dispatch({ type: 'hourlyForecast/setIncludedHours', payload: newIncludedHours });
    };

    const handleVisibleUnitsDailyChange = (unit) => {
        if (!visibleUnitsDaily) return;
        
        const newVisibleUnits = {
            ...visibleUnitsDaily,
            [unit]: !visibleUnitsDaily[unit],
        };
        dispatch({ type: 'dailyForecast/setVisibleUnitsDaily', payload: newVisibleUnits });
    }

    const handleVisibleUnitsHourlyChange = (unit) => {
        if (!visibleUnitsHourly) return;
        
        const newVisibleUnits = {
            ...visibleUnitsHourly,
            [unit]: !visibleUnitsHourly[unit],
        };
        dispatch({ type: 'hourlyForecast/setVisibleUnitsHourly', payload: newVisibleUnits });
    }

    return (
        <>
        <Helmet>
            <title>Settings | Oreca Pro</title>
            <link rel="canonical" href={window.location.href} />
        </Helmet>
        <Header />
        <MobileNav />
        <div className='py-[80px] px-5 max-w-[1100px] mx-auto'>
            <h1 className='text-2xl font-semibold mb-2 md:mt-8'>Settings</h1>
            
            {/* Cookie Settings Link */}
            <div className='max-w-[700px] mx-auto mt-8 mb-6'>
                <div className='bg-custom-charcoal/50 p-4 rounded-lg border border-gray-700'>
                    <div className='flex items-center justify-between'>
                        <div>
                            <h3 className='text-lg font-semibold text-white mb-1'>Cookie Preferences</h3>
                            <p className='text-sm text-gray-400'>Manage your cookie and analytics settings</p>
                        </div>
                        <Link 
                            to="/cookie-policy" 
                            className='bg-custom-light-blue text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors text-sm'
                        >
                            Manage Cookies
                        </Link>
                    </div>
                </div>
            </div>
            
            <div className='max-w-[700px] mx-auto mt-10'>
                <div className='mt-6 mb-12'>
                    <p className="text-lg ">Daily Forecast Units</p>
                    <div className='mt-6 mb-6 flex items-center gap-3 flex-wrap'>
                        <Pill 
                            active={visibleUnitsDaily?.wave_height_max} 
                            onClick={() => handleVisibleUnitsDailyChange('wave_height_max')}
                        >
                            Wave Height
                        </Pill>
                        <Pill 
                            active={visibleUnitsDaily?.wave_period_max} 
                            onClick={() => handleVisibleUnitsDailyChange('wave_period_max')}
                        >
                            Wave Period
                        </Pill>
                        <Pill 
                            active={visibleUnitsDaily?.wave_direction_dominant} 
                            onClick={() => handleVisibleUnitsDailyChange('wave_direction_dominant')}
                        >
                            Wave Direction
                        </Pill>
                        <Pill 
                            active={visibleUnitsDaily?.wind_speed_10m_max} 
                            onClick={() => handleVisibleUnitsDailyChange('wind_speed_10m_max')}
                        >
                            Wind Speed
                        </Pill>
                        <Pill 
                            active={visibleUnitsDaily?.wind_direction_10m_dominant} 
                            onClick={() => handleVisibleUnitsDailyChange('wind_direction_10m_dominant')}
                        >
                            Wind Direction
                        </Pill>
                        <Pill 
                            active={visibleUnitsDaily?.overall_rating_score} 
                            onClick={() => handleVisibleUnitsDailyChange('overall_rating_score')}
                        >
                            Rating
                        </Pill>
                    </div>
                </div>


                <div className='mt-6 mb-12'>
                    <p className="text-lg ">Hourly Forecast Units</p>
                    <div className='mt-6 mb-6 flex items-center gap-3 flex-wrap'>
                        <Pill 
                            active={visibleUnitsHourly?.overall_rating_score} 
                            onClick={() => handleVisibleUnitsHourlyChange('overall_rating_score')}
                        >
                            Rating
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.wave_height} 
                            onClick={() => handleVisibleUnitsHourlyChange('wave_height')}
                        >
                            Wave Height
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.swell_wave_height} 
                            onClick={() => handleVisibleUnitsHourlyChange('swell_wave_height')}
                        >
                            Swell Wave Height
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.swell_wave_period} 
                            onClick={() => handleVisibleUnitsHourlyChange('swell_wave_period')}
                        >
                            Swell Wave Period
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.swell_wave_direction} 
                            onClick={() => handleVisibleUnitsHourlyChange('swell_wave_direction')}
                        >
                            Swell Wave Direction
                        </Pill>
                        
                        <Pill 
                            active={visibleUnitsHourly?.wind_direction_10m} 
                            onClick={() => handleVisibleUnitsHourlyChange('wind_direction_10m')}
                        >
                            Wind Direction
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.wind_speed_10m} 
                            onClick={() => handleVisibleUnitsHourlyChange('wind_speed_10m')}
                        >
                            Wind Speed 10m
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.wind_speed_80m} 
                            onClick={() => handleVisibleUnitsHourlyChange('wind_speed_80m')}
                        >
                            Wind Speed 80m
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.wind_gusts_10m} 
                            onClick={() => handleVisibleUnitsHourlyChange('wind_gusts_10m')}
                        >
                            Wind Gusts
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.temperature_2m} 
                            onClick={() => handleVisibleUnitsHourlyChange('temperature_2m')}
                        >
                            Temperature
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.rain} 
                            onClick={() => handleVisibleUnitsHourlyChange('rain')}
                        >
                            Rain
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.cloud_cover} 
                            onClick={() => handleVisibleUnitsHourlyChange('cloud_cover')}
                        >
                            Cloud Cover
                        </Pill>
                        <Pill 
                            active={visibleUnitsHourly?.sea_level_height_msl} 
                            onClick={() => handleVisibleUnitsHourlyChange('sea_level_height_msl')}
                        >
                            Sea Level Height (tide)
                        </Pill>
                    </div>
                </div>
                <div className='mt-6 mb-12'>
                    <p className="text-lg ">Included Hours in Hourly Forecast</p>
                    <div className='mt-6 mb-6 flex items-center gap-3 flex-wrap'>
                        {['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'].map((hour) => (
                            <Pill 
                                key={hour}
                                active={includedHours?.includes(hour)} 
                                onClick={() => handleIncludedHoursChange(hour)}
                            >
                                {hour}
                            </Pill>
                        ))}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Options;
